import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import ReportCard from 'component/reports/ReportCard';
import { reportService } from '../../services/reportService';
import { useLocation, useSearchParams } from "react-router-dom";
import Transport from "../../assets/images/report_img.jpg";
import { proxiedLogoUrl } from "utils/logoUrl";
import { translateSectorLabel } from "utils/sectorLabel";

const PLACEHOLDER_IMG = "https://news-demo.dataflock.de/api/news/placeholder";
const normalizeSectors = (value) => {
  const input = Array.isArray(value) ? value : (value ? [value] : []);
  return input
    .map((sector) => {
      if (typeof sector === 'string') return sector;
      if (sector && typeof sector === 'object') return sector.name || sector.slug || '';
      return '';
    })
    .filter(Boolean);
};

const mapReport = (r = {}, t) => ({
  id: r.id ?? r.report_id ?? r._id,
  title: r.title || r.name || t("reports.untitled"),
  summary: r.summary || r.description || '',
  image_url: r.hero_image_url || r.image_url || r.cover_image || PLACEHOLDER_IMG,
  match_rate: r.match_rate ?? r.score ?? null,
  sectors: normalizeSectors(r.sectors || (r.sector ? [r.sector] : [])),
  recent_at: r.created_at || r.published_at || r.updated_at,
  reading_time_minutes: r.reading_time_minutes || r.read_time_minutes || r.reading_minutes || null,
});

const ReportOverview = () => {
  const { t, i18n } = useTranslation();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const sourceId = searchParams.get("source_id");
  const sourceName = searchParams.get("source_name") || location.state?.sourceName;
  const sourceImage = searchParams.get("source_image");
  const sectorId = searchParams.get("sector_id") || searchParams.get("field_id");
  const sectorName = searchParams.get("sector_name") || searchParams.get("field_name");
  const sectorImage = searchParams.get("sector_image");

  const title = sourceName || translateSectorLabel(sectorName, i18n.resolvedLanguage || i18n.language) || t("reports.pageTitle");
  const heroImage = proxiedLogoUrl(sourceImage) || sectorImage || PLACEHOLDER_IMG;

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        let data;
        if (sourceId) {
          data = await reportService.listSourceReports(sourceId, { limit: 30 });
        } else {
          data = await reportService.listReports({
            limit: 30,
            sector_id: sectorId || undefined,
          });
        }
        const arr = Array.isArray(data) ? data : [];
        setReports(arr.map((item) => mapReport(item, t)));
      } catch (e) {
        setError(e?.message || t("reports.error.load"));
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [sectorId, sourceId, t]);

  return (
    <div className="main__content__box">
      <div className="container-fluid">
        <div className="content_wrap">
          <div className="reportPoster_img_wrap position-relative">
            <img
              src={heroImage}
              alt={t("reports.pageTitle")}
              className="h-100 w-100 object-fit-cover"
              onError={(e) => {
                if (e.currentTarget.dataset.fallback) return;
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src = PLACEHOLDER_IMG;
              }}
            />
            <div className="report-hero-overlay" />
            <div className="title top-50 translate-middle-y report_overview_posterImg position-absolute text-center w-100">
              <h4 className="text-white mb-0">{title}</h4>
            </div>
          </div>
        </div>

        <div className="content_wrap pt-5">
          <div className="reports_overview_tabsBox">
            {error && <div className="text-danger mb-3">{error}</div>}
            {loading && <div className="text-muted mb-3">{t("reports.loading")}</div>}
            <div className="accordion report_overview_accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className="accordion-button show"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#item-0"
                  >
                    {t("reports.overview.allReports")}
                  </button>
                </h2>

                <div
                  id="item-0"
                  className="accordion-collapse collapse show"
                  data-bs-parent="#accordionExample"
                >
                  <div className="accordion-body">
                    <div className="row g-4">
                      {reports.map((report, index) => (
                        <div className="col-md-4" key={report.id || index}>
                          <ReportCard
                            value={index + 1}
                            title={report.title}
                            summary={report.summary}
                            _id={report.id}
                            progress={report.match_rate}
                            image_url={report.image_url}
                            sectors={report.sectors}
                            created_at={report.recent_at}
                            reading_time_minutes={report.reading_time_minutes}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportOverview;
